import React, { useState, useEffect, useCallback } from 'react';
import { settingsApi } from '../api/settingsApi';
import type { KeyStatus } from '../types/contracts';
import Spinner     from '../components/Spinner';
import ErrorBanner from '../components/ErrorBanner';
import { haptic }  from '../telegram';

const KNOWN_PROVIDERS = ['openai', 'anthropic', 'google', 'mistral'];

// ---- styles ----
const pageSt: React.CSSProperties = { minHeight: '100vh', display: 'flex', flexDirection: 'column' };
const headerSt: React.CSSProperties = {
  position: 'sticky', top: 0, zIndex: 10,
  padding: '12px 16px',
  background: 'var(--tg-theme-header-bg-color)',
  borderBottom: '1px solid rgba(255,255,255,0.08)',
};
const h1St: React.CSSProperties  = { margin: 0, fontSize: 18, fontWeight: 600 };
const scrollSt: React.CSSProperties = { flex: 1, overflowY: 'auto', padding: '16px 16px 80px' };
const sectionLabelSt: React.CSSProperties = {
  fontSize: 12, textTransform: 'uppercase', letterSpacing: 1,
  color: 'var(--tg-theme-hint-color)', marginBottom: 8,
};
const sectionCardSt: React.CSSProperties = {
  background: 'var(--tg-theme-secondary-bg-color)',
  borderRadius: 20, overflow: 'hidden', marginBottom: 24,
};
const rowSt: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '12px 16px',
};
const dividerSt: React.CSSProperties = {
  height: 1, margin: '0 16px', background: 'rgba(255,255,255,0.06)',
};
const providerNameSt: React.CSSProperties = {
  fontSize: 15, fontWeight: 500, textTransform: 'capitalize',
};
const keyStatusSt = (hasKey: boolean): React.CSSProperties => ({
  fontSize: 12, marginTop: 2,
  color: hasKey ? 'var(--tg-theme-accent-text-color)' : 'var(--tg-theme-hint-color)',
});
const actionsSt: React.CSSProperties = { display: 'flex', gap: 8 };
const btnEditSt: React.CSSProperties = {
  padding: '6px 14px', borderRadius: 12,
  background: 'var(--tg-theme-button-color)', color: 'var(--tg-theme-button-text-color)',
  border: 'none', fontSize: 13, cursor: 'pointer',
};
const btnDelSt: React.CSSProperties = {
  padding: '6px 10px', borderRadius: 12,
  background: 'rgba(255,69,58,0.2)', color: 'var(--tg-theme-destructive-text-color)',
  border: 'none', fontSize: 13, cursor: 'pointer',
};
const inlineEditSt: React.CSSProperties = {
  padding: '8px 16px 12px', display: 'flex', gap: 8,
};
const inputSt: React.CSSProperties = {
  flex: 1, borderRadius: 12, padding: '8px 14px',
  background: 'var(--tg-theme-bg-color)', color: 'var(--tg-theme-text-color)',
  border: '1px solid rgba(255,255,255,0.12)', fontSize: 14, outline: 'none',
};
const btnSaveSt: React.CSSProperties = {
  padding: '8px 16px', borderRadius: 12,
  background: 'var(--tg-theme-button-color)', color: 'var(--tg-theme-button-text-color)',
  border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer',
};
const btnCancelSt: React.CSSProperties = {
  padding: '8px 12px', borderRadius: 12,
  background: 'rgba(255,255,255,0.08)', color: 'var(--tg-theme-text-color)',
  border: 'none', fontSize: 14, cursor: 'pointer',
};
const infoBannerSt: React.CSSProperties = {
  borderRadius: 20, padding: 14,
  background: 'var(--tg-theme-secondary-bg-color)',
  fontSize: 12, color: 'var(--tg-theme-hint-color)',
  lineHeight: 1.6,
};
const centeredSt: React.CSSProperties = { display: 'flex', justifyContent: 'center', alignItems: 'center', height: 160 };

export default function Settings() {
  const [keys,         setKeys]         = useState<KeyStatus[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState<string | null>(null);
  const [editProvider, setEditProvider] = useState<string | null>(null);
  const [keyInput,     setKeyInput]     = useState('');
  const [saving,       setSaving]       = useState(false);

  const load = useCallback(async () => {
    try { setLoading(true); setError(null); setKeys(await settingsApi.getKeyStatus()); }
    catch (e) { setError(String(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSet = async () => {
    if (!editProvider || !keyInput.trim()) return;
    setSaving(true);
    try {
      await settingsApi.setKey({ provider: editProvider, key: keyInput.trim() });
      setKeys((p) => p.map((k) => k.provider === editProvider ? { ...k, has_key: true } : k));
      setEditProvider(null); setKeyInput('');
      haptic('medium');
    } catch (e) { setError(String(e)); }
    finally { setSaving(false); }
  };

  const handleDelete = async (provider: string) => {
    if (!window.confirm(`Удалить ключ ${provider}?`)) return;
    haptic('medium');
    setKeys((p) => p.map((k) => k.provider === provider ? { ...k, has_key: false } : k));
    try { await settingsApi.deleteKey(provider); } catch { load(); }
  };

  if (loading) return <div style={centeredSt}><Spinner size={32} /></div>;

  const merged = KNOWN_PROVIDERS.map((p) => {
    const found = keys.find((k) => k.provider === p);
    return found ?? { provider: p, has_key: false };
  });

  return (
    <div style={pageSt}>
      <div style={headerSt}>
        <h1 style={h1St}>Настройки</h1>
      </div>

      <div style={scrollSt}>
        {error && <ErrorBanner message={error} onRetry={load} />}

        <p style={sectionLabelSt}>API-ключи (BYOK)</p>
        <div style={sectionCardSt}>
          {merged.map((k, idx) => (
            <div key={k.provider}>
              {idx > 0 && <div style={dividerSt} />}
              <div style={rowSt}>
                <div>
                  <p style={providerNameSt}>{k.provider}</p>
                  <p style={keyStatusSt(k.has_key)}>
                    {k.has_key ? '• ключ задан' : '— не задан'}
                  </p>
                </div>
                <div style={actionsSt}>
                  <button style={btnEditSt}
                    onClick={() => { setEditProvider(k.provider); setKeyInput(''); haptic('light'); }}>
                    {k.has_key ? 'Изм.' : 'Задать'}
                  </button>
                  {k.has_key && (
                    <button style={btnDelSt} onClick={() => handleDelete(k.provider)}>✕</button>
                  )}
                </div>
              </div>

              {editProvider === k.provider && (
                <div style={inlineEditSt}>
                  <input type="password" style={inputSt}
                    placeholder={`sk-... (${k.provider})`}
                    value={keyInput}
                    onChange={(e) => setKeyInput(e.target.value)}
                    autoFocus />
                  <button style={btnSaveSt} onClick={handleSet} disabled={saving || !keyInput.trim()}>
                    {saving ? '…' : '✔'}
                  </button>
                  <button style={btnCancelSt} onClick={() => setEditProvider(null)}>×</button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={infoBannerSt}>
          <p>🔒 Ключи хранятся в зашифрованном виде (AES-256-GCM) на сервере.</p>
          <p>📋 Макс. 5 пользователей на инстанс (MAX_USERS = 5).</p>
        </div>
      </div>
    </div>
  );
}
